import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { EventPageType } from '@graphql/generated-base';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from '@shared/core';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import type { GuestSiteQueryParams } from './useGuestSiteQueryParams';
import { useGuestSiteQueryParams } from './useGuestSiteQueryParams';
import { useGuestSitePageViewedDuration } from './useGuestSitePageViewedDuration';
import { getPageInfo, getSlugFromPathname } from './getPageInfo';

export { getPageInfo, getSlugFromPathname };

export const useGuestSitePageChanged = (pages: ReadonlyArray<EventPageFragment>) => {
  const telemetry = useGuestSiteTelemetry() as unknown as Record<string, { enter: (role: string, args: GuestSiteQueryParams) => void }>;
  const [currentPageSlug, setCurrentPageSlug] = useState<string>();
  const { isAdmin } = useEventUserRole();
  const history = useHistory();
  const queryParams = useGuestSiteQueryParams();
  const { trackPageVisitDuration } = useGuestSitePageViewedDuration(pages);
  const { defaultPage } = useDefaultPage(pages);

  const trackPage = useCallback(
    locationPathName => {
      let { pageSlug, pageFound } = getPageInfo(pages, locationPathName);

      // Root URL (no slug segment) — fall back to the default page (welcome or first page).
      // useDefaultPage already handles feature-flag-aware welcome page detection.
      if (!pageFound && defaultPage) {
        const { pagePath } = getSlugFromPathname(locationPathName);
        if (!pagePath) {
          pageFound = defaultPage;
          pageSlug = pageFound.type === EventPageType.custom ? EventPageType.custom : pageFound.pageSlug;
        }
      }

      if (pageFound && pageSlug && telemetry[pageSlug] && currentPageSlug !== pageFound.pageSlug) {
        telemetry[pageSlug].enter(isAdmin ? 'admin' : 'guest', { ...queryParams, slug: pageFound.pageSlug });
        setCurrentPageSlug(pageFound.pageSlug);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pages, history, currentPageSlug, defaultPage],
  );

  useEffect(() => {
    if (history.location.pathname) {
      trackPage(history.location.pathname);
      trackPageVisitDuration(history.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname]);
};
